// Gestion de usuarios (SOLO admin): crear usuarios con permisos por modulo,
// editar permisos y activar/desactivar.
//
// Nadie tiene clave: todos entran con Google, y lo que se les carga aca es el
// correo de esa cuenta. Entre todos no pueden pasar de MAX_USUARIOS activos
// (la cuenta protegida, que es la de soporte, no cuenta).

import { api } from "../api";
import { iconoLapiz, iconoMas } from "../iconos";
import { MAX_USUARIOS } from "../../lib/types";
import type { Usuario, Cliente } from "../../lib/types";

// Tiene que estar alineada con MODULOS de functions/_utils/permisos.ts: si un
// modulo esta en el backend y no aca, no hay forma de darle ese permiso a
// nadie desde la pantalla.
const MODULOS: Array<{ clave: string; nombre: string }> = [
  { clave: "pedido", nombre: "Pedido" },
  { clave: "pedidosweb", nombre: "Pedidos web" },
  { clave: "pedidos", nombre: "Pedidos (lista)" },
  { clave: "anular", nombre: "Anular pedidos" },
  { clave: "produccion", nombre: "Producción" },
  { clave: "cuentas", nombre: "Cuentas" },
  { clave: "listas", nombre: "Lista de precios" },
  { clave: "subas", nombre: "Subas" },
  { clave: "informes", nombre: "Informes" },
  { clave: "clientes", nombre: "Clientes" },
  { clave: "productos", nombre: "Productos" },
  { clave: "categorias", nombre: "Categorías" },
];

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

function permisosDe(u: Usuario): string[] {
  try {
    const p: unknown = JSON.parse(u.permisos || "[]");
    return Array.isArray(p) ? p.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function grillaPermisos(idPrefix: string, marcados: string[]): string {
  return `<div class="grid grid-cols-2 gap-1 sm:grid-cols-3" data-grid-permisos>
    ${MODULOS.map(
      (m) => `<label class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-crema-50">
        <input type="checkbox" value="${m.clave}" id="${idPrefix}_${m.clave}" class="h-4 w-4 accent-masa-600" ${marcados.includes(m.clave) ? "checked" : ""} />
        <span>${m.nombre}</span></label>`,
    ).join("")}
  </div>`;
}

/** Casillas de locales para los usuarios de local. */
function grillaLocales(idPrefix: string, locales: Cliente[], marcados: number[]): string {
  if (locales.length === 0) {
    return `<p class="text-sm text-masa-500">No hay locales cargados. Crealos primero en Clientes.</p>`;
  }
  return `<div class="grid grid-cols-1 gap-1 sm:grid-cols-2" data-grid-locales>
    ${locales
      .map(
        (c) => `<label class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-crema-50">
        <input type="checkbox" value="${c.id}" id="${idPrefix}_loc_${c.id}" class="h-4 w-4 accent-masa-600" ${marcados.includes(c.id) ? "checked" : ""} />
        <span>${esc(c.nombre)} <span class="text-masa-400">${esc(c.codigo)}</span></span></label>`,
      )
      .join("")}
  </div>`;
}

/** Ids de los locales que maneja el usuario (vienen como "1,2"). */
function localesDe(u: Usuario): number[] {
  return (u.locales_ids ?? "")
    .split(",")
    .map((s) => Number(s))
    .filter((n) => Number.isInteger(n) && n > 0);
}

export async function renderUsuarios(contenedor: HTMLElement): Promise<void> {
  let usuarios: Usuario[] = [];
  let locales: Cliente[] = [];
  try {
    locales = await api.get<Cliente[]>("/api/clientes?activo=1");
  } catch {
    /* si falla, la seccion de locales queda vacia con su aviso */
  }

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Usuarios</h1>
      <p class="subtitulo">Crea usuarios y elegí que puede hacer cada uno. Solo el administrador ve esta pantalla.</p>
      <p id="cupo" class="mt-1 text-sm text-masa-500"></p>
    </div>

    <details class="tarjeta mb-4">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-bold uppercase tracking-wide text-masa-600">
        <span class="text-masa-500">${iconoMas}</span> Nuevo usuario
      </summary>
      <form id="f" class="mt-4 space-y-3">
        <div>
          <span class="etiqueta">Tipo de usuario</span>
          <div class="flex flex-wrap gap-3">
            <label class="flex cursor-pointer items-center gap-2 text-sm">
              <input type="radio" name="tipo" value="INTERNO" checked class="h-4 w-4 accent-masa-600" />
              <span><b>Interno</b> — personal de la confitería</span></label>
            <label class="flex cursor-pointer items-center gap-2 text-sm">
              <input type="radio" name="tipo" value="CLIENTE" class="h-4 w-4 accent-masa-600" />
              <span><b>De local</b> — entra al portal a hacer pedidos</span></label>
          </div>
        </div>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label><span class="etiqueta">Usuario</span><input id="n_usuario" class="campo" placeholder="ej: repartidor" /></label>
          <label class="sm:col-span-2"><span class="etiqueta">Correo de Google</span><input id="n_email" type="email" class="campo" placeholder="ej: encargada@gmail.com" autocapitalize="none" autocorrect="off" spellcheck="false" /></label>
          <label id="n_admin_wrap" class="flex items-end gap-2 pb-2">
            <input id="n_admin" type="checkbox" class="h-4 w-4 accent-masa-600" />
            <span class="text-sm font-medium">Es administrador (ve todo)</span></label>
        </div>
        <div id="n_permisos_wrap">
          <span class="etiqueta">Permisos (que puede usar)</span>
          ${grillaPermisos("n", [])}
        </div>
        <div id="n_locales_wrap" class="hidden">
          <span class="etiqueta">Locales que puede gestionar</span>
          ${grillaLocales("n", locales, [])}
          <p class="mt-1 text-xs text-masa-500">Se puede tildar más de uno: la misma persona puede pedir para varios locales.</p>
          <p class="mt-2 rounded-lg bg-crema-100 p-2 text-xs text-masa-600">No lleva permisos: solo ve el portal de pedidos de esos locales.</p>
        </div>
        <p class="rounded-lg bg-crema-100 p-2 text-xs text-masa-600">Nadie tiene clave: se entra tocando <b>Entrar con Google</b> con ese correo. El nombre de usuario es solo para identificarlo acá y en la auditoría.</p>
        <div class="flex items-center gap-3">
          <button class="btn-primario">${iconoMas} Crear usuario</button>
          <span id="n_err" class="text-sm text-red-600"></span>
        </div>
      </form>
    </details>

    <div class="tarjeta overflow-x-auto p-0">
      <table class="tabla">
        <thead><tr><th>Usuario</th><th>Tipo</th><th>Permisos</th><th>Estado</th><th class="text-right">Acciones</th></tr></thead>
        <tbody id="tbody"></tbody>
      </table>
    </div>
    <dialog id="dlg"></dialog>`;

  const tbody = contenedor.querySelector("#tbody") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;
  const nErr = contenedor.querySelector("#n_err") as HTMLElement;
  const cupo = contenedor.querySelector("#cupo") as HTMLElement;
  const nAdmin = contenedor.querySelector("#n_admin") as HTMLInputElement;
  const nPermisosWrap = contenedor.querySelector("#n_permisos_wrap") as HTMLElement;

  const nLocalesWrap = contenedor.querySelector("#n_locales_wrap") as HTMLElement;
  const nAdminWrap = contenedor.querySelector("#n_admin_wrap") as HTMLElement;

  nAdmin.addEventListener("change", () => {
    nPermisosWrap.classList.toggle("opacity-40", nAdmin.checked);
    nPermisosWrap.querySelectorAll<HTMLInputElement>("input").forEach((c) => (c.disabled = nAdmin.checked));
  });

  // Un usuario de local no tiene permisos por modulo ni puede ser admin:
  // se le muestran los locales en lugar de la grilla de permisos.
  const tipoElegido = (): string =>
    (contenedor.querySelector('input[name="tipo"]:checked') as HTMLInputElement)?.value ?? "INTERNO";

  contenedor.querySelectorAll<HTMLInputElement>('input[name="tipo"]').forEach((r) =>
    r.addEventListener("change", () => {
      const esLocal = tipoElegido() === "CLIENTE";
      nPermisosWrap.classList.toggle("hidden", esLocal);
      nAdminWrap.classList.toggle("hidden", esLocal);
      nLocalesWrap.classList.toggle("hidden", !esLocal);
      if (esLocal) nAdmin.checked = false;
    }),
  );

  function pintar(): void {
    tbody.innerHTML = usuarios
      .map((u) => {
        const esLocal = u.rol === "CLIENTE";
        const perms = esLocal
          ? u.locales || "sin locales"
          : u.es_admin
            ? "todos"
            : permisosDe(u).map((p) => MODULOS.find((m) => m.clave === p)?.nombre ?? p).join(", ") || "ninguno";
        const tipo = esLocal
          ? `<span class="rounded-full bg-crema-200 px-2 py-0.5 text-xs font-semibold text-masa-800">LOCAL</span>`
          : u.es_admin
            ? `<span class="rounded-full bg-masa-100 px-2 py-0.5 text-xs font-semibold text-masa-800">ADMIN</span>`
            : `<span class="text-sm text-masa-500">usuario</span>`;
        return `<tr class="${u.activo ? "" : "opacity-50"}">
          <td class="font-medium">${esc(u.usuario)}${
            u.protegido
              ? ` <span class="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800" title="Cuenta de soporte: solo ella puede modificarse y no cuenta para el máximo de usuarios">PROTEGIDO</span>`
              : ""
          }${
            u.email
              ? `<br><span class="text-xs font-normal text-masa-500">${esc(u.email)}</span>`
              : `<br><span class="text-xs font-normal text-amber-700">sin correo: no puede entrar</span>`
          }</td>
          <td>${tipo}</td>
          <td class="max-w-[16rem] text-sm text-masa-600">${esc(perms)}</td>
          <td>${u.activo ? "Activo" : "Desactivado"}</td>
          <td class="text-right"><button class="chip-accion" data-editar="${u.id}" title="Editar">${iconoLapiz}</button></td>
        </tr>`;
      })
      .join("");
  }

  async function cargar(): Promise<void> {
    tbody.innerHTML = `<tr><td colspan="5" class="py-6 text-center text-masa-400">Cargando...</td></tr>`;
    usuarios = await api.get<Usuario[]>("/api/usuarios");
    pintar();
    // Los desactivados no ocupan lugar: para sumar a alguien alcanza con dar
    // de baja a otro.
    // Misma regla que exigirCupoDeUsuarios(): la cuenta protegida no cuenta.
    const activos = usuarios.filter((u) => u.activo && !u.protegido).length;
    const lleno = activos >= MAX_USUARIOS;
    const libres = MAX_USUARIOS - activos;
    cupo.innerHTML = `
      <b>${activos} de ${MAX_USUARIOS}</b> usuarios en uso${
        lleno
          ? ". <b>Llegaste al máximo</b>: para sumar uno hay que desactivar otro."
          : ` — ${libres === 1 ? "queda 1 lugar" : `quedan ${libres} lugares`}.`
      }
      <span class="mt-0.5 block text-xs font-normal text-masa-500">
        El plan incluye hasta ${MAX_USUARIOS} usuarios activos, entre internos y locales. Los
        desactivados no cuentan, y la cuenta de <b>soporte</b> (la que dice PROTEGIDO) tampoco.
      </span>`;
    cupo.className = `mt-2 rounded-lg border px-3 py-2 text-sm ${
      lleno ? "border-amber-300 bg-amber-50 text-amber-900" : "border-crema-200 bg-papel text-masa-700"
    }`;
  }

  const form = contenedor.querySelector("#f") as HTMLFormElement;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    nErr.textContent = "";
    const usuario = (contenedor.querySelector("#n_usuario") as HTMLInputElement).value.trim();
    const email = (contenedor.querySelector("#n_email") as HTMLInputElement).value.trim();
    const rol = tipoElegido();
    const esLocal = rol === "CLIENTE";
    const esAdmin = esLocal ? false : nAdmin.checked;
    const permisos = [...nPermisosWrap.querySelectorAll<HTMLInputElement>("input:checked")].map((c) => c.value);
    const localesElegidos = [...nLocalesWrap.querySelectorAll<HTMLInputElement>("input:checked")].map((c) =>
      Number(c.value),
    );
    if (usuario.length < 3) { nErr.textContent = "El usuario debe tener al menos 3 caracteres"; return; }
    if (!email.includes("@")) { nErr.textContent = "Escribí el correo de Google con el que va a entrar"; return; }
    if (esLocal && localesElegidos.length === 0) { nErr.textContent = "Elegí al menos un local"; return; }
    if (!esLocal && !esAdmin && permisos.length === 0) { nErr.textContent = "Elegí al menos un permiso (o marca admin)"; return; }
    try {
      await api.post("/api/usuarios", {
        usuario,
        email,
        rol,
        es_admin: esAdmin,
        permisos,
        locales: localesElegidos,
      });
      form.reset();
      nAdmin.dispatchEvent(new Event("change"));
      contenedor.querySelectorAll<HTMLInputElement>('input[name="tipo"]')[0]?.dispatchEvent(new Event("change"));
      await cargar();
    } catch (e) {
      nErr.textContent = e instanceof Error ? e.message : "Error";
    }
  });

  tbody.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest("[data-editar]");
    if (!btn) return;
    const u = usuarios.find((x) => x.id === Number(btn.getAttribute("data-editar")));
    if (u) abrirEdicion(u);
  });

  function abrirEdicion(u: Usuario): void {
    const esLocal = u.rol === "CLIENTE";
    dlg.innerHTML = `
      <form method="dialog" class="tarjeta w-[30rem] max-w-full border-0 shadow-none">
        <h2 class="mb-1 text-lg font-bold">Editar usuario</h2>
        <p class="mb-4 text-sm text-masa-500">${esc(u.usuario)}${esLocal ? " — usuario de local" : ""}</p>
        <div class="space-y-3">
          ${
            esLocal
              ? `<div>
                  <span class="etiqueta">Locales que puede gestionar</span>
                  ${grillaLocales("e", locales, localesDe(u))}
                </div>`
              : `<label class="flex items-center gap-2">
            <input id="e_admin" type="checkbox" class="h-4 w-4 accent-masa-600" ${u.es_admin ? "checked" : ""} />
            <span class="text-sm font-medium">Es administrador (ve todo)</span></label>
          <div id="e_permisos_wrap" class="${u.es_admin ? "opacity-40" : ""}">
            <span class="etiqueta">Permisos</span>
            ${grillaPermisos("e", permisosDe(u))}
          </div>`
          }
          <label class="flex items-center gap-2">
            <input id="e_activo" type="checkbox" class="h-4 w-4 accent-masa-600" ${u.activo ? "checked" : ""} />
            <span class="text-sm">Activo (puede entrar al sistema)</span></label>
          <label class="block"><span class="etiqueta">Correo de Google con el que entra</span>
            <input id="e_email" type="email" class="campo" value="${esc(u.email ?? "")}" placeholder="ej: encargada@gmail.com" autocapitalize="none" autocorrect="off" spellcheck="false" /></label>
          <p class="text-xs text-masa-500">No tiene clave. Si le cambiás el correo, la cuenta de antes deja de entrar en el acto.</p>
        </div>
        <p id="e_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button value="cancel" class="btn-sec">Cancelar</button>
          <button id="e_guardar" value="ok" class="btn-primario">Guardar</button>
        </div>
      </form>`;
    const eErr = dlg.querySelector("#e_err") as HTMLElement;
    const eAdmin = dlg.querySelector("#e_admin") as HTMLInputElement | null;
    const eWrap = dlg.querySelector("#e_permisos_wrap") as HTMLElement | null;
    if (eAdmin && eWrap) {
      eAdmin.addEventListener("change", () => {
        eWrap.classList.toggle("opacity-40", eAdmin.checked);
        eWrap.querySelectorAll<HTMLInputElement>("input").forEach((c) => (c.disabled = eAdmin.checked));
      });
      if (u.es_admin) eWrap.querySelectorAll<HTMLInputElement>("input").forEach((c) => (c.disabled = true));
    }

    dlg.querySelector("#e_guardar")?.addEventListener("click", async (ev) => {
      ev.preventDefault();
      eErr.textContent = "";
      const emailNuevo = (dlg.querySelector("#e_email") as HTMLInputElement | null)?.value.trim() ?? "";
      const quedaActivo = (dlg.querySelector("#e_activo") as HTMLInputElement).checked;
      if (quedaActivo && !emailNuevo.includes("@")) {
        eErr.textContent = "Escribí el correo de Google: sin él no puede entrar";
        return;
      }
      const permisos = eWrap
        ? [...eWrap.querySelectorAll<HTMLInputElement>("input:checked")].map((c) => c.value)
        : [];
      const localesElegidos = [
        ...dlg.querySelectorAll<HTMLInputElement>("[data-grid-locales] input:checked"),
      ].map((c) => Number(c.value));
      if (esLocal && localesElegidos.length === 0) {
        eErr.textContent = "Un usuario de local necesita al menos un local";
        return;
      }
      try {
        await api.put(`/api/usuarios/${u.id}`, {
          es_admin: eAdmin?.checked ?? false,
          permisos,
          locales: esLocal ? localesElegidos : null,
          activo: quedaActivo ? 1 : 0,
          email: emailNuevo || null,
        });
        dlg.close();
        await cargar();
      } catch (e) {
        eErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });
    dlg.showModal();
  }

  await cargar();
}
